import {
  addDays,
  endOfWeek,
  isAfter,
  isBefore,
  isSameWeek,
  startOfWeek,
} from 'date-fns';
import { useTaskTodayControls, useUpdateTask } from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import {
  PlannedOccurrence,
  ScheduleEntryState,
} from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { formatDateKey } from 'shared/utils/format-date-key';
import { getStartTillEndLabel } from 'shared/utils/get-start-till-end-label';
import {
  createPlannedOccurrenceForDate,
  deletePlannedOccurrenceForDate,
  getScheduleByDate,
  getScheduleEndDate,
} from 'shared/utils/schedules';

import { UpcomingWeek as UpcomingWeekType } from '../../types';
import { useData } from '../../use-data';
import * as Styled from './upcoming-week.style';
import { WeekDay } from './week-day';

export type UpcomingWeekProps = {
  task: Task;
  week: UpcomingWeekType;
};

export const UpcomingWeek: React.FC<UpcomingWeekProps> = ({ task, week }) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn } = useData();

  const { onComplete } = useTaskTodayControls();
  const { submit: updateTask } = useUpdateTask();

  const label = useMemo(() => {
    const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
    if (isSameWeek(week.date, today, dateFnsOptions)) {
      return t('dialogs.task-detail.meta.upcoming.this-week.label');
    }

    if (isSameWeek(week.date, addDays(today, 7), dateFnsOptions)) {
      return t('dialogs.task-detail.meta.upcoming.next-week.label');
    }

    return getStartTillEndLabel({
      startDate: startOfWeek(week.date, dateFnsOptions),
      endDate: endOfWeek(week.date, dateFnsOptions),
      today,
      t,
    });
  }, [t, today, week.date, weekStartsOn]);

  const onAddEntry = (date: Timestamp) => {
    if (!task.schedules?.length) {
      return;
    }

    const scheduleForDate = getScheduleByDate(task.schedules, date);
    const schedules = createPlannedOccurrenceForDate(task.schedules, date, {
      timeslot: scheduleForDate?.timeslot,
      today,
      weekStartsOn,
      originalDate: date,
    });

    // update the schedule with a `timeslot` based on the event
    updateTask({ id: task.id, schedules });
  };

  const onChangeEntry = (entry: PlannedOccurrence) => {
    if (!task.schedules?.length) {
      return;
    }

    const schedules = createPlannedOccurrenceForDate(
      task.schedules,
      entry.date,
      {
        timeslot: entry.timeslot,
        today,
        weekStartsOn,
        id: entry.id,
        originalDate: entry.date,
      },
    );

    // update the schedule with a `timeslot` based on the event
    updateTask({ id: task.id, schedules });
  };

  const onDeleteEntry = (date: Timestamp) => {
    const schedules = task.schedules
      ?.toSorted(
        (scheduleA, scheduleB) =>
          scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
      )
      .map((schedule, index, arr) => {
        // get the previous schedule end date (if there is a previous schedule)
        const prevSchedule = arr[index - 1];
        const prevScheduleEndDate = prevSchedule
          ? getScheduleEndDate(prevSchedule)
          : undefined;
        const scheduleEndDate = getScheduleEndDate(schedule);

        // if the date is not part of the current schedule, the schedule is good as is
        if (
          (prevScheduleEndDate && isBefore(date, prevScheduleEndDate)) ||
          (scheduleEndDate && isAfter(date, scheduleEndDate))
        ) {
          return schedule;
        }

        return deletePlannedOccurrenceForDate(schedule, date, {
          weekStartsOn,
          today,
        });
      });

    // update the schedule with a `timeslot` based on the event
    updateTask({ id: task.id, schedules });
  };

  const onCompleteDate = (date: Timestamp) => {
    const daySchedule = getScheduleByDate(task.schedules, date);
    onComplete(task, ScheduleEntryState.Complete, {
      referenceDate: date,
      toggleDayEntry: !!daySchedule?.frequency,
      keepDetailOpen: true,
    });
  };

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.Title>{label}</Styled.Title>
      </Styled.Header>
      <Styled.List>
        {week.days.map(({ date, entry }) => (
          <Styled.ListItem key={formatDateKey(date)}>
            <WeekDay
              task={task}
              date={date}
              entry={entry}
              onAddEntry={onAddEntry}
              onChangeEntry={onChangeEntry}
              onDeleteEntry={onDeleteEntry}
              onComplete={onCompleteDate}
            />
          </Styled.ListItem>
        ))}
      </Styled.List>
    </Styled.Container>
  );
};
